import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown, User, LayoutDashboard, Key, LogOut } from "lucide-react";
import { logout, getUserData } from "../utils/authUtils";

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [clinicName, setClinicName] = useState("");
  const [userRole, setUserRole] = useState("");
  const profileRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Verificar si el usuario está logueado y obtener nombre de clínica
  useEffect(() => {
    const checkLoginStatus = () => {
      const userData = getUserData();
      setIsLoggedIn(!!userData);

      if (userData) {
        setClinicName(userData.companyName || "Mi Clínica");
        setUserRole(userData.role || "");
      }
    };

    checkLoginStatus();

    // Escuchar cambios en localStorage
    window.addEventListener('storage', checkLoginStatus);
    return () => window.removeEventListener('storage', checkLoginStatus);
  }, []);

  // Cerrar menú desplegable al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    closeMenu();
    logout();
  };

  return (
    <nav className="bg-white py-2 sm:py-3 shadow-md sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo y nombre */}
          <div className="flex items-center space-x-2 sm:space-x-4">
            <img
              src="/assets/logo.png"
              alt="ImageClec Logo"
              className="h-12 w-auto sm:h-16 lg:h-20"
            />
            <span className="text-[#144A7B] text-lg sm:text-xl lg:text-2xl font-bold tracking-widest font-[Montserrat]">
              IMAGECLEC
            </span>
          </div>

          {/* Menú de escritorio */}
          <div className="hidden md:flex items-center text-[#144A7B] space-x-4 lg:space-x-6 text-sm lg:text-base">
            <Link to="/" className="hover:text-[#A7D3F3] transition-colors duration-200">
              Inicio
            </Link>
            <Link to="/centros-medicos" className="hover:text-[#A7D3F3] transition-colors duration-200">
              Centros Médicos
            </Link>
            {/*<Link to="/ecografistas" className="hover:text-[#A7D3F3] transition-colors duration-200">
              Ecografistas
            </Link>*/}
            <Link to="/faq" className="hover:text-[#A7D3F3] transition-colors duration-200">
              FAQ
            </Link>
            
            {/*<Link to="/register" className="text-white hover:text-[#A7D3F3] transition-colors duration-200">
              Registrarse
            </Link>*/}
            <Link to="/contact" className="hover:text-[#A7D3F3] transition-colors duration-200">
              Contacto
            </Link>
            {isLoggedIn ? (
              <div className="relative" ref={profileRef}>
                <button
                  onClick={() => setIsProfileOpen(!isProfileOpen)}
                  className="bg-[#144A7B] text-white px-4 py-2 rounded-lg hover:bg-[#A7D3F3] hover:text-[#144A7B] transition-all duration-200 font-medium shadow-md hover:shadow-lg flex items-center gap-2"
                >
                  <User className="h-4 w-4" />
                  <span>{clinicName}</span>
                  <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isProfileOpen ? 'rotate-180' : ''}`} />
                </button>

                {isProfileOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
                    <Link
                      to={userRole === 'ADMIN' ? '/admin/dashboard' : '/dashboard'}
                      onClick={() => setIsProfileOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 text-[#144A7B] hover:bg-gray-50 transition-colors duration-200"
                    >
                      <LayoutDashboard className="h-5 w-5" />
                      <span className="font-medium">Dashboard</span>
                    </Link>

                    <Link
                      to="/change-password"
                      onClick={() => setIsProfileOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 text-[#144A7B] hover:bg-gray-50 transition-colors duration-200"
                    >
                      <Key className="h-5 w-5" />
                      <span className="font-medium">Cambiar Contraseña</span>
                    </Link>

                    <hr className="my-2 border-gray-200" />

                    <button
                      onClick={() => {
                        setIsProfileOpen(false);
                        handleLogout();
                      }}
                      className="flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-red-50 transition-colors duration-200 w-full text-left"
                    >
                      <LogOut className="h-5 w-5" />
                      <span className="font-medium">Cerrar Sesión</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                className="bg-[#144A7B] text-white px-4 py-2 rounded-lg hover:bg-[#A7D3F3] hover:text-[#144A7B] transition-all duration-200 font-medium shadow-md hover:shadow-lg"
              >
                Iniciar Sesión
              </Link>
            )}
          </div>

          {/* Botón de menú móvil */}
          <div className="md:hidden">
            <button
              onClick={toggleMenu}
              className="text-[#144A7B] hover:text-[#A7D3F3] transition-colors duration-200 p-2"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Menú móvil */}
        {isMenuOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 bg-white border-t border-gray-200 mt-2 rounded-lg shadow-lg">
              <Link
                to="/"
                onClick={closeMenu}
                className="block px-3 py-2 text-[#144A7B] hover:text-[#A7D3F3] hover:bg-gray-50 rounded-md transition-colors duration-200 text-base"
              >
                Inicio
              </Link>
              <Link
                to="/centros-medicos"
                onClick={closeMenu}
                className="block px-3 py-2 text-[#144A7B] hover:text-[#A7D3F3] hover:bg-gray-50 rounded-md transition-colors duration-200 text-base"
              >
                Centros Médicos
              </Link>
              {/*<Link
                to="/ecografistas"
                onClick={closeMenu}
                className="block px-3 py-2 text-[#144A7B] hover:text-[#A7D3F3] hover:bg-gray-50 rounded-md transition-colors duration-200 text-base"
              >
                Ecografistas
              </Link>*/}
              <Link
                to="/faq"
                onClick={closeMenu}
                className="block px-3 py-2 text-[#144A7B] hover:text-[#A7D3F3] hover:bg-gray-50 rounded-md transition-colors duration-200 text-base"
              >
                FAQ
              </Link>
              <Link
                to="/contact"
                onClick={closeMenu}
                className="block px-3 py-2 text-[#144A7B] hover:text-[#A7D3F3] hover:bg-gray-50 rounded-md transition-colors duration-200 text-base"
              >
                Contacto
              </Link>
              {isLoggedIn ? (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <div className="px-3 py-2 mb-2">
                    <div className="flex items-center gap-2 text-[#144A7B] font-semibold">
                      <User className="h-5 w-5" />
                      <span>{clinicName}</span>
                    </div>
                  </div>

                  <Link
                    to={userRole === 'ADMIN' ? '/admin/dashboard' : '/dashboard'}
                    onClick={closeMenu}
                    className="flex items-center gap-3 px-3 py-2 text-[#144A7B] hover:bg-gray-50 rounded-md transition-colors duration-200"
                  >
                    <LayoutDashboard className="h-5 w-5" />
                    <span>Dashboard</span>
                  </Link>

                  <Link
                    to="/change-password"
                    onClick={closeMenu}
                    className="flex items-center gap-3 px-3 py-2 text-[#144A7B] hover:bg-gray-50 rounded-md transition-colors duration-200"
                  >
                    <Key className="h-5 w-5" />
                    <span>Cambiar Contraseña</span>
                  </Link>

                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-3 px-3 py-2 mt-2 text-red-600 hover:bg-red-50 rounded-md transition-colors duration-200 w-full text-left"
                  >
                    <LogOut className="h-5 w-5" />
                    <span className="font-medium">Cerrar Sesión</span>
                  </button>
                </div>
              ) : (
                <Link
                  to="/login"
                  onClick={closeMenu}
                  className="block px-3 py-2 mx-2 mt-2 text-center bg-[#144A7B] text-white rounded-lg hover:bg-[#A7D3F3] hover:text-[#144A7B] transition-all duration-200 font-medium shadow-md"
                >
                  Iniciar Sesión
                </Link>
              )}
              {/*<Link
                to="/register"
                onClick={closeMenu}
                className="block px-3 py-2 text-[#144A7B] hover:text-[#A7D3F3] hover:bg-gray-50 rounded-md transition-colors duration-200 text-base"
              >
                Registrarse
              </Link>*/}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar; 